import { useEffect, useRef } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAccountUpdate } from '@consumer-frontend/nc-api';
import {
  deepEqual,
  IncomeDataT,
  IncomeDetailsFormT,
  incomeTransform,
  initialAlertBannerProps,
  isEmployed,
  isEmptyObject,
  modifiedAccountFields,
  payAmountValidations,
  paymentDaysOptions,
  PaymentDaysValueT,
  paymentFrequencyOptions,
  PaymentFrequencyValueT,
  paymentMethodOptions,
  PaymentMethodValueT,
  registrationTransform,
  roundToTwoDecimals,
  somethingWentWrongAlertBannerProps,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { NcIncomeDetailsMonthlyFields } from './NcIncomeDetailsMonthlyFields';
import { NcIncomeDetailsPayDayFields } from './NcIncomeDetailsPayDayFields';
import { useApplyRouting } from '../../routing';
import {
  accountAtom,
  alertBannerAtom,
  doesServerHaveIncomeDataAtom,
  incomeDataAtom,
  registrationDataAtom,
} from '../../store';
import { NcButton } from '../NcButton';
import { NcForm } from '../NcForm';
import { NcInput } from '../NcInput';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';
import { NcProgressBar } from '../NcProgressBar';
import { NcSelect } from '../NcSelect';

import '@consumer-bootstrap/brands/netcredit/apply/income_details.scss';

export const NcIncomeDetailsPage: React.FC = () => {
  const setAlertBanner = useSetAtom(alertBannerAtom);
  const account = useAtomValue(accountAtom);
  const incomeData = useAtomValue(incomeDataAtom);
  const setDoesServerHaveIncomeData = useSetAtom(doesServerHaveIncomeDataAtom);
  const {
    handleBack,
    handleContinue,
    progress,
  } = useApplyRouting();
  const { trigger: updateAccount, isMutating: isAccountUpdating } = useAccountUpdate();
  const registrationData = useAtomValue(registrationDataAtom);

  const source = incomeData.source ?? 'other';

  const defaultPayDay = incomeData.paymentSchedule === 'same_day_each_month'
    ? undefined
    : incomeData.day;

  const defaultMonthlyWeekDay = incomeData.paymentSchedule === 'same_day_particular_week'
    ? incomeData.day
    : undefined;

  const updateAccountData = async (newIncomeData: IncomeDataT): Promise<boolean> => {
    // only need to update account info if it has changed
    const modifiedFields = account ? modifiedAccountFields(account, registrationData) : undefined;
    const isIncomeModified = !account || !deepEqual(account.income, newIncomeData);

    const updatedFields = {
      ...(modifiedFields ? registrationTransform.serialize(modifiedFields) : {}),
      ...(isIncomeModified ? incomeTransform.serialize(newIncomeData) : {}),
    };

    if (isEmptyObject(updatedFields)) {
      return true;
    }

    const accountUpdated = await updateAccount(updatedFields);

    // account updated will be undefined for request errors, object for request success
    return !!accountUpdated;
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IncomeDetailsFormT>({
    defaultValues: {
      amount: incomeData.amount,
      frequency: incomeData.frequency,
      method: incomeData.method,
      monthlyPaymentDay: incomeData.monthlyPaymentDay,
      monthlyWeekDay: defaultMonthlyWeekDay,
      nextPayday: incomeData.nextPayday,
      payDay: defaultPayDay,
      paymentDays: incomeData.paymentDays,
      paymentSchedule: incomeData.paymentSchedule,
      paymentWeek: incomeData.paymentWeek,
    },
  });

  const selectedFrequency = watch('frequency');
  const selectedPayDay = watch('payDay');
  const payDayRef = useRef(selectedPayDay);

  const showPayDayFields = selectedFrequency === 'biweekly' || selectedFrequency === 'weekly';
  const showPaymentDays = selectedFrequency === 'twice_monthly';
  const showMonthly = selectedFrequency === 'monthly';

  // Reset next payday when pay day changes so a stale value doesn't persist
  useEffect(
    () => {
      // Don't reset it when going back to income details from consents page or returning from income source.
      if (selectedPayDay === payDayRef.current) { return; }

      // Initialize payday ref when the user hasn't selected a pay day yet (e.g., on initial render).
      if (!payDayRef.current) {
        payDayRef.current = selectedPayDay;

        return;
      }

      setValue('nextPayday', undefined);
      payDayRef.current = selectedPayDay;
    },
    [selectedPayDay, setValue],
  );

  const onSubmit: SubmitHandler<IncomeDetailsFormT> = async ({
    amount,
    monthlyWeekDay,
    payDay,
    ...data
  }) => {
    setAlertBanner(initialAlertBannerProps);

    const newIncomeData: IncomeDataT = {
      ...data,
      amount: roundToTwoDecimals(amount),
      benefits: incomeData.benefits,
      day: monthlyWeekDay ?? payDay,
      employment: incomeData.employment,
      source,
    };

    // update account for 'returning customer' flow
    const accountUpdateSuccess = await updateAccountData(newIncomeData);

    if (!accountUpdateSuccess) {
      setAlertBanner(somethingWentWrongAlertBannerProps);

      return;
    }

    setDoesServerHaveIncomeData(true);

    await handleContinue({}, newIncomeData);
  };

  const paymentMethodValidations = {
    validate: (option: PaymentMethodValueT) => {
      return paymentMethodOptions.some((paymentMethod) => paymentMethod.value === option);
    },
  };

  const frequencyValidations = {
    validate: (option: PaymentFrequencyValueT) => {
      return paymentFrequencyOptions.some((paymentFrequency) => paymentFrequency.value === option);
    },
  };

  const paymentDaysValidations = {
    shouldUnregister: true,
    validate: (option?: PaymentDaysValueT) => {
      return paymentDaysOptions.some((days) => days.value === option);
    },
  };

  const payAmountLabel = isEmployed(source)
    ? 'Pay Per Paycheck (After Taxes)'
    : 'Pay Per Benefits Check (After Taxes)';

  const payAmountOptions = {
    ...payAmountValidations,
    valueAsNumber: true,
  };

  const isLoading = isAccountUpdating;

  return (
    <NcPageWithAlertBanner>
      <Container className="nc-income-details-page" hasBoxShadow>
        <NcProgressBar progress={progress} />

        <h1 className="h3">Income Details</h1>

        <NcForm onSubmit={handleSubmit(onSubmit)}>
          <NcInput
            {...register('amount', { ...payAmountOptions })}
            errorMessage={errors.amount?.message}
            id="pay-amount-input"
            label={payAmountLabel}
            min={0.01}
            prefix="$"
            step={0.01}
            type="number"
          />

          <NcSelect
            {...register('method', paymentMethodValidations)}
            errorMessage={errors.method && 'Select a payment method.'}
            id="payment-method"
            label={<span className="form-label">Payment Method</span>}
            options={paymentMethodOptions}
            placeholder="Select payment method"
          />

          <NcSelect
            {...register('frequency', frequencyValidations)}
            errorMessage={errors.frequency && 'Select how often you get paid.'}
            id="payment-frequency"
            label={<span className="form-label">Payment Frequency</span>}
            options={paymentFrequencyOptions}
            placeholder="Select payment frequency"
          />

          {showPayDayFields && (
            <NcIncomeDetailsPayDayFields
              errors={errors}
              isBiweekly={selectedFrequency === 'biweekly'}
              register={register}
              watch={watch}
            />
          )}

          {showPaymentDays && (
            <NcSelect
              {...register('paymentDays', paymentDaysValidations)}
              errorMessage={errors.paymentDays && 'Select the days of the month you get paid.'}
              id="payment-days"
              label={<span className="form-label">Payment Days</span>}
              options={paymentDaysOptions}
              placeholder="Select days"
            />
          )}

          {showMonthly && (
            <NcIncomeDetailsMonthlyFields
              errors={errors}
              register={register}
              watch={watch}
            />
          )}

          <Fieldset className="cta-buttons">
            <NcButton
              loading={isLoading}
              type="submit"
            >
              Continue
            </NcButton>
            <NcButton
              disabled={isLoading}
              variant="outline-primary"
              onClick={handleBack}
            >
              Back
            </NcButton>
          </Fieldset>
        </NcForm>
      </Container>
    </NcPageWithAlertBanner>
  );
};
